import Drawer from '@mui/material/Drawer';
import Grid from '@mui/material/Grid';
import Collapse from '@mui/material/Collapse';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link as RouterLink, useMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import { resolvePath, useQuery, sso as ssoPath } from '../routes';
import {
  dashboard,
  licenseUtilization,
  supportManagement,
  userManagement,
  testCloudDashboard,
  accountSetting,
  paymentMethod,
  accountSubscriptionManagement,
  testOpsPlatformCheckout,
  organizationManagement,
  securitySettings,
  projectManagement,
  maintenanceProcess,
  userGroup,
} from './routes';

import {
  AccountSettingActiveIcon,
  AccountSettingInActiveIcon,
  SidebarCloseIcon,
  SidebarOpenIcon,
  LicenseUtilizationInactiveIcon,
  LicenseManagementInactiveIcon,
  UserManagementActiveIcon,
  UserManagementInactiveIcon,
  PaymentMethodActiveIcon,
  PaymentMethodInactiveIcon,
  SubscriptionManagementInactiveIcon,
  SubscriptionManagementActiveIcon,
  OrganizationManagementActiveIcon,
  OrganizationManagementInactiveIcon,
  SecuritySettingsActiveIcon,
  SecuritySettingsInactiveIcon,
  TestOpsIcon,
  KSEIcon,
  SupportManagementActiveIcon,
  SupportManagementInactiveIcon,
  TestCloudIcon,
  ActivateLicense,
  EmailConfiguration,
  TeamManagementInactiveIcon,
  ProjectManagementInactiveIcon,
  ProjectManagementActiveIcon,
  UserGroupIconActive,
  UserGroupIconInActive,
} from './CustomIcon';
import {
  isOrganizationBillingManagerOrOwner,
  OrganizationRole,
  isOrganizationManager,
  OrganizationMigrationStatus,
} from '../models';
import { fromAuth, fromCurrentOrgUser, fromOrganizations, fromTestOpsProject } from '../store';
import { useConfig } from '../config';
import { useLaunchDarkly } from '../launchdarkly';
import BannerSideBar from './banners/BannerSideBar';
import { doGetOrgMigrationStatus } from '../store/organizationsSlice';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100vh',
  },
  menuContainerOpen: {
    marginTop: '-0.75rem',
    overflow: 'auto',
    overflowX: 'hidden',
  },
  menuContainerClose: {
    overflow: 'hidden',
  },
  menuItem: {
    marginLeft: '.5rem',
    width: theme.spacing(35.5),
    paddingBottom: '.5rem',
    paddingTop: '.5rem',
    paddingLeft: theme.spacing(2),
    marginBottom: '.5rem',
    fontWeight: theme.typography.fontWeightMedium,
    '&.Mui-disabled': {
      backgroundColor: theme.palette.background.paper,
    },
    '&:hover': {
      borderRadius: '.25rem',
    },
    '&.Mui-selected': {
      borderRadius: '.25rem',
    },
    justifyContent: 'left',
  },
  subMenuItem: {
    width: theme.spacing(31.5),
    marginLeft: '2.5rem',
    paddingBottom: '.5rem',
    paddingTop: '.5rem',
    marginBottom: '.5rem',
    paddingLeft: theme.spacing(2),
    '&.Mui-disabled': {
      backgroundColor: theme.palette.background.paper,
    },
    '&:hover': {
      borderRadius: '.25rem',
    },
    '&.Mui-selected': {
      borderRadius: '.25rem',
    },
    justifyContent: 'left',
  },
  sideTitle: {
    marginLeft: '1rem',
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    textTransform: 'uppercase',
  },
  sideSubtitle: {
    paddingLeft: '2rem',
    marginTop: '.2rem',
    marginBottom: '.5rem',
    textTransform: 'uppercase',
  },
  drawer: {
    border: 'none',
  },
  drawerOpen: {
    width: theme.spacing(37.5),
    marginLeft: '0rem',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.complex,
    }),
    overflow: 'visible',
  },
  drawerClose: {
    width: theme.spacing(37.5),
    marginLeft: '-18rem',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.complex,
    }),
    overflow: 'visible',
  },
  sidebarCollapseButton: {
    marginLeft: '3rem',
    marginTop: '.25rem',
  },
  drawCloseSidebarCollapseButton: {
    '&.MuiButton-root': {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
  },
  productUtilExpand: {
    transform: 'rotate(90deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    '&.MuiSvgIcon-root': {
      marginLeft: '5.5rem',
    },
  },
  subsMngtExpand: {
    transform: 'rotate(90deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    '&.MuiSvgIcon-root': {
      marginLeft: '2rem',
    },
  },
  subsMngtExpandClose: {
    transform: 'rotate(270deg)',
  },
  sidebarButtonIcon: {
    width: '1.75rem',
    height: '1.75rem',
  },
  menuIcon: {
    width: '1.25rem',
    height: '1.25rem',
  },
  menuListItemIcon: {
    minWidth: '0rem',
    paddingRight: '.75rem',
  },
  sideTitleContainer: {
    marginTop: theme.spacing(7.5),
  },
}));

const isOwnerOrAdmin = (role: OrganizationRole) => [
  OrganizationRole.OWNER,
  OrganizationRole.ADMIN,
].includes(role);

const Sidebar = () => {
  const classes = useStyles();
  const intl = useIntl();
  const { queryDictionary } = useQuery();
  const theme = useTheme();
  const isSmallScreenSize = useMediaQuery(theme.breakpoints.down('md'));
  const { config } = useConfig();
  const { flags } = useLaunchDarkly();
  const [open, setOpen] = useState(true);
  const [productUtilChecked, setProductUtilChecked] = useState(true);
  const orgId = useQuery().get('orgId');
  const user = useSelector(fromAuth.selectUser);
  const currentOrgUser = useSelector(fromCurrentOrgUser.selectByOrganizationIdAndEmail(
    user?.email || '',
    Number(orgId),
  ));
  const isRootOrAdmin = useSelector(fromAuth.selectIsRootOrAdmin);
  const projectCountByOrgId = useSelector(fromTestOpsProject.selectProjectCount);
  const orgMigrationStatus = useSelector(
    fromOrganizations.selectOrgMigrationStatusById(Number(orgId)),
  );
  const isLoading = useSelector(
    fromOrganizations.selectLoading(doGetOrgMigrationStatus.typePrefix),
  );
  const isAccountSettingSelected = useMatch(accountSetting.path)?.pathname !== undefined;
  const isPaymentMethodSelected = useMatch(paymentMethod.path)?.pathname !== undefined;
  const isSubscriptionSelected = useMatch(
    accountSubscriptionManagement.path,
  )?.pathname !== undefined;
  const isPurchaseSelected = useMatch(testOpsPlatformCheckout.path)?.pathname !== undefined;
  const isSubscriptionManagementSelected = isSubscriptionSelected || isPurchaseSelected;
  const isUserManagementSelected = useMatch(userManagement.path)?.pathname !== undefined;
  const isUserGroupSelected = useMatch(userGroup.path)?.pathname !== undefined;
  const isLicenseUtilizationSelected = useMatch(licenseUtilization.path)?.pathname !== undefined;
  const isPlatformDashboardSelected = useMatch(dashboard.path)?.pathname !== undefined;
  const isTestCloudDashboardSelected = useMatch(testCloudDashboard.path)?.pathname !== undefined;
  const isSupportManagementSelected = useMatch(supportManagement.path)?.pathname !== undefined;
  const isSecuritySettingsSelected = useMatch(securitySettings.path)?.pathname !== undefined;
  const isOrgManagementSelected = useMatch(organizationManagement.path)?.pathname !== undefined;
  const isProjectManagementSelected = useMatch(projectManagement.path)?.pathname !== undefined;

  useEffect(() => {
    if (isSmallScreenSize) {
      setOpen(false);
    }
  }, [isSmallScreenSize]);
  // Hide sidebar if the user is not system's root/admin and only a MEMBER
  if (!isRootOrAdmin && (currentOrgUser ? currentOrgUser.role === OrganizationRole.MEMBER : true)) {
    return null;
  }

  const handleSidebar = () => {
    if (open) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  };

  const handleProductUtilClick = () => {
    setProductUtilChecked(!productUtilChecked);
  };

  const goToProjects = () => {
    let projectUri: string;
    const oldUI = resolvePath(ssoPath, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${orgId}/teams/projects` });
    const newUI = resolvePath(projectManagement, undefined, { orgId: `${orgId}` });
    const maintenanceUI = resolvePath(maintenanceProcess, undefined);
    if (orgMigrationStatus === OrganizationMigrationStatus.MIGRATED) {
      projectUri = newUI;
    } else if (flags?.projectStandardizeEnabled) {
      projectUri = maintenanceUI;
    } else if (flags?.projectManagementNewUIEnabled) {
      projectUri = newUI;
    } else {
      projectUri = oldUI;
    }
    return projectUri;
  };

  const canAccessSubscriptionAndPaymentInfo = (
    isRootOrAdmin || isOrganizationBillingManagerOrOwner(currentOrgUser!!.role)
  ) && !config?.onpremise
    && ((flags && flags.subscriptionAndPaymentMethodDisabled === false) || flags === undefined);

  return (
    <Drawer
      variant="permanent"
      classes={{
        root: clsx({
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        }),
        paper: clsx({
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        }),
        paperAnchorDockedLeft: classes.drawer,
      }}
    >
      <Grid container alignItems="center" className={classes.sideTitleContainer}>
        <Grid item xs={9}>
          <Typography
            className={classes.sideTitle}
            variant="h3"
          >
            {intl.formatMessage({ id: 'layout.sidebar.testopsadmin' })}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <IconButton
            onClick={handleSidebar}
            style={{ backgroundColor: 'transparent' }}
            className={clsx(classes.sidebarCollapseButton, {
              [classes.drawCloseSidebarCollapseButton]: !open,
            })}
            size="large"
          >
            {
              open
                ? <SidebarCloseIcon className={classes.sidebarButtonIcon} />
                : <SidebarOpenIcon className={classes.sidebarButtonIcon} />
            }
          </IconButton>
        </Grid>
      </Grid>
      <Grid className={clsx(classes.menuContainerOpen, {
        [classes.menuContainerClose]: !open,
      })}
      >
        {canAccessSubscriptionAndPaymentInfo && (
        <BannerSideBar
          orgId={Number(orgId)}
          accountId={currentOrgUser?.organization.accountId}
        />
        )}
        <MenuItem
          className={classes.menuItem}
          disabled={!open}
          onClick={handleProductUtilClick}
        >
          <ListItemIcon className={classes.menuListItemIcon}>
            <LicenseUtilizationInactiveIcon className={classes.menuIcon} />
          </ListItemIcon>
          {intl.formatMessage({ id: 'layout.sidebar.product_utilization' })}
          <NavigateNextIcon
            color="action"
            fontSize="small"
            style={open ? {} : { display: 'none' }}
            className={clsx(classes.productUtilExpand, {
              [classes.subsMngtExpandClose]: productUtilChecked,
            })}
          />
        </MenuItem>
        <Collapse in={productUtilChecked}>
          {(isRootOrAdmin || isOwnerOrAdmin(currentOrgUser!!.role)) && (
            <MenuItem
              className={classes.subMenuItem}
              disabled={!open}
              component={RouterLink}
              selected={isLicenseUtilizationSelected}
              to={resolvePath(licenseUtilization, undefined, { orgId: Number(orgId) })}
            >
              <ListItemIcon className={classes.menuListItemIcon}>
                <KSEIcon className={classes.menuIcon} />
              </ListItemIcon>
              {intl.formatMessage({ id: 'layout.sidebar.katalon_studio' })}
            </MenuItem>
          )}
          <MenuItem
            className={classes.subMenuItem}
            disabled={!open}
            component={RouterLink}
            selected={isPlatformDashboardSelected}
            to={resolvePath(dashboard, undefined, { orgId: Number(orgId) })}
          >
            <ListItemIcon className={classes.menuListItemIcon}>
              <TestOpsIcon className={classes.menuIcon} />
            </ListItemIcon>
            {intl.formatMessage({ id: 'layout.sidebar.katalon_testops' })}
          </MenuItem>
          <MenuItem
            className={classes.subMenuItem}
            disabled={!open}
            component={RouterLink}
            selected={isTestCloudDashboardSelected}
            to={resolvePath(testCloudDashboard, undefined, { orgId: Number(orgId) })}
          >
            <ListItemIcon className={classes.menuListItemIcon}>
              <TestCloudIcon className={classes.menuIcon} />
            </ListItemIcon>
            {intl.formatMessage({ id: 'layout.testopssidebar.testcloud' })}
          </MenuItem>
        </Collapse>
        <MenuItem
          className={classes.menuItem}
          disabled={!open}
          component={RouterLink}
          to={resolvePath(ssoPath, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${orgId}/admin/license_keys/kse` })}
        >
          <ListItemIcon className={classes.menuListItemIcon}>
            <LicenseManagementInactiveIcon className={classes.menuIcon} />
          </ListItemIcon>
          {intl.formatMessage({ id: 'layout.testopssidebar.licenses' })}
        </MenuItem>
        {(isRootOrAdmin || isOwnerOrAdmin(currentOrgUser!!.role)) && (
          <MenuItem
            className={classes.menuItem}
            disabled={!open}
            selected={isUserManagementSelected}
            component={RouterLink}
            to={resolvePath(userManagement, undefined, { orgId: `${orgId}` })}
          >
            <ListItemIcon className={classes.menuListItemIcon}>
              {isUserManagementSelected
                ? <UserManagementActiveIcon className={classes.menuIcon} />
                : <UserManagementInactiveIcon className={classes.menuIcon} />}
            </ListItemIcon>
            {intl.formatMessage({ id: 'layout.testopssidebar.user_management' })}
          </MenuItem>
        )}
        {(isRootOrAdmin || isOwnerOrAdmin(currentOrgUser!!.role))
          && flags?.isUserGroupEnable
          && (
            <MenuItem
              className={classes.menuItem}
              disabled={!open}
              selected={isUserGroupSelected}
              component={RouterLink}
              to={resolvePath(userGroup, undefined, { orgId: `${orgId}` })}
            >
              <ListItemIcon className={classes.menuListItemIcon}>
                {isUserGroupSelected
                  ? <UserGroupIconActive className={classes.menuIcon} />
                  : <UserGroupIconInActive className={classes.menuIcon} />}
              </ListItemIcon>
              {intl.formatMessage({ id: 'layout.new.sidebar.user.group' })}
            </MenuItem>
          )}
        {canAccessSubscriptionAndPaymentInfo && (
        <>
          <MenuItem
            className={classes.menuItem}
            disabled={!open}
            component={RouterLink}
            selected={isPaymentMethodSelected}
            to={resolvePath(
              paymentMethod,
              undefined,
              { orgId: Number(orgId), accountId: currentOrgUser?.organization.accountId },
            )}
          >
            <ListItemIcon className={classes.menuListItemIcon}>
              {
                    isPaymentMethodSelected
                      ? <PaymentMethodActiveIcon className={classes.menuIcon} />
                      : <PaymentMethodInactiveIcon className={classes.menuIcon} />
                  }
            </ListItemIcon>
            {intl.formatMessage({ id: 'layout.testopssidebar.payment_method' })}
          </MenuItem>
          <MenuItem
            className={classes.menuItem}
            disabled={!open}
            component={RouterLink}
            selected={isSubscriptionManagementSelected}
            to={resolvePath(
              accountSubscriptionManagement,
              undefined,
              { orgId: Number(orgId), accountId: currentOrgUser?.organization.accountId },
            )}
          >
            <ListItemIcon className={classes.menuListItemIcon}>
              {
                    isSubscriptionManagementSelected
                      ? <SubscriptionManagementActiveIcon className={classes.menuIcon} />
                      : <SubscriptionManagementInactiveIcon className={classes.menuIcon} />
                  }
            </ListItemIcon>
            {intl.formatMessage({ id: 'layout.sidebar.subscription_management' })}
          </MenuItem>
        </>
        )}
        {(isRootOrAdmin || isOwnerOrAdmin(currentOrgUser!!.role)) && (
          <>
            <MenuItem
              className={classes.menuItem}
              disabled={!open}
              component={RouterLink}
              selected={isOrgManagementSelected}
              to={resolvePath(
                organizationManagement,
                undefined,
                { orgId: Number(orgId), accountId: currentOrgUser?.organization.accountId },
              )}
            >
              <ListItemIcon className={classes.menuListItemIcon}>
                {isOrgManagementSelected
                  ? <OrganizationManagementActiveIcon className={classes.menuIcon} />
                  : <OrganizationManagementInactiveIcon className={classes.menuIcon} />}
              </ListItemIcon>
              {intl.formatMessage({ id: 'layout.testopssidebar.organization_management' })}
            </MenuItem>
            {!config?.onpremise && config?.serviceCloudEnabled && (
              <MenuItem
                className={classes.menuItem}
                disabled={!open}
                component={RouterLink}
                selected={isSupportManagementSelected}
                to={resolvePath(supportManagement, undefined, queryDictionary())}
              >
                <ListItemIcon className={classes.menuListItemIcon}>
                  {isSupportManagementSelected
                    ? <SupportManagementActiveIcon className={classes.menuIcon} />
                    : <SupportManagementInactiveIcon className={classes.menuIcon} />}
                </ListItemIcon>
                {intl.formatMessage({ id: 'layout.sidebar.support_management' })}
              </MenuItem>
            )}
            <MenuItem
              className={classes.menuItem}
              selected={isSecuritySettingsSelected}
              disabled={!open}
              component={RouterLink}
              to={resolvePath(
                securitySettings,
                undefined,
                { orgId: Number(orgId), accountId: currentOrgUser?.organization.accountId },
              )}
            >
              <ListItemIcon className={classes.menuListItemIcon}>
                {isSecuritySettingsSelected
                  ? <SecuritySettingsActiveIcon className={classes.menuIcon} />
                  : <SecuritySettingsInactiveIcon className={classes.menuIcon} />}
              </ListItemIcon>
              {intl.formatMessage({ id: 'layout.testopssidebar.security.setting' })}
            </MenuItem>
          </>
        )}

        { !isLoading && (orgMigrationStatus === undefined
          || orgMigrationStatus !== OrganizationMigrationStatus.MIGRATED)
          && (
            <MenuItem
              id="layout.sidebar.menuitem.team_management"
              className={classes.menuItem}
              disabled={!open}
              component={RouterLink}
              to={
                flags?.projectStandardizeEnabled
                  ? resolvePath(
                    maintenanceProcess,
                    undefined,
                  )
                  : resolvePath(
                    ssoPath,
                    undefined,
                    { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${orgId}/teams/settings` },
                  )
              }
            >
              <ListItemIcon className={classes.menuListItemIcon}>
                <TeamManagementInactiveIcon className={classes.menuIcon} />
              </ListItemIcon>
              {intl.formatMessage({ id: 'layout.sidebar.team_management' })}
            </MenuItem>
          )}

        {(isRootOrAdmin
        || isOwnerOrAdmin(currentOrgUser!!.role)
        || (projectCountByOrgId[Number(orgId)] > 0
          && currentOrgUser?.role === OrganizationRole.BILLING_MANAGER)
        )
        && (
          <MenuItem
            id="layout.sidebar.menuitem.project_management"
            className={classes.menuItem}
            selected={isProjectManagementSelected}
            disabled={!open}
            component={RouterLink}
            to={goToProjects()}
          >
            <ListItemIcon className={classes.menuListItemIcon}>
              { isProjectManagementSelected
                ? <ProjectManagementActiveIcon className={classes.menuIcon} />
                : <ProjectManagementInactiveIcon className={classes.menuIcon} />}
            </ListItemIcon>
            {intl.formatMessage({ id: 'layout.sidebar.project_management' })}
          </MenuItem>
        )}
        {(isRootOrAdmin || isOrganizationManager(currentOrgUser!!.role))
          && (
            <MenuItem
              className={classes.menuItem}
              component={RouterLink}
              disabled={!open}
              selected={isAccountSettingSelected}
              to={resolvePath(
                accountSetting,
                undefined,
                { orgId: Number(orgId), accountId: currentOrgUser?.organization.accountId },
              )}
            >
              <ListItemIcon className={classes.menuListItemIcon}>
                {isAccountSettingSelected
                  ? <AccountSettingActiveIcon className={classes.menuIcon} />
                  : <AccountSettingInActiveIcon className={classes.menuIcon} />}
              </ListItemIcon>
              {intl.formatMessage({ id: 'layout.account_sidebar.account_settings' })}
            </MenuItem>
          )}
        {(isRootOrAdmin
          || isOwnerOrAdmin(currentOrgUser!!.role))
          && config?.onpremise && (
            <MenuItem
              className={classes.menuItem}
              disabled={!open}
              component={RouterLink}
              to={resolvePath(ssoPath, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${orgId}/teams/testops` })}
            >
              <ListItemIcon className={classes.menuListItemIcon}>
                <ActivateLicense className={classes.menuIcon} />
              </ListItemIcon>
              {intl.formatMessage({ id: 'layout.sidebar.activate_license' })}
            </MenuItem>
        )}
        {(isRootOrAdmin || isOwnerOrAdmin(currentOrgUser!!.role)) && config?.onpremise && (
          <MenuItem
            className={classes.menuItem}
            disabled={!open}
            component={RouterLink}
            to={resolvePath(ssoPath, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${orgId}/admin/configuration` })}
          >
            <ListItemIcon className={classes.menuListItemIcon}>
              <EmailConfiguration className={classes.menuIcon} />
            </ListItemIcon>
            {intl.formatMessage({ id: 'layout.sidebar.email_configuration' })}
          </MenuItem>
        )}
      </Grid>
    </Drawer>
  );
};

export default Sidebar;
